import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  deleteNotification,
  listNotifications,
  markAllRead,
  markRead,
} from "@/api/notifications";
import { useNotifications } from "@/hooks/useNotifications";
import {
  NotificationItem,
  destinationFor,
} from "@/components/notifications/NotificationItem";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import type { NotificationRead } from "@/api/types";

export function NotificationBell() {
  const navigate = useNavigate();
  const { project } = useCurrentProject();
  const { unreadCount, setUnreadCount, refetch } = useNotifications();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRead[]>([]);
  const [loading, setLoading] = useState(false);

  // Trigger a one-shot shake when unreadCount jumps up (polling delivered new ones).
  const prevCountRef = useRef(unreadCount);
  const [shakeKey, setShakeKey] = useState(0);
  useEffect(() => {
    if (unreadCount > prevCountRef.current) {
      setShakeKey((k) => k + 1);
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listNotifications({ limit: 20 });
      setItems(res.items);
    } catch {
      // Silent — bell just shows empty state.
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch the list whenever the popover opens.
  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  async function handleClick(n: NotificationRead) {
    setOpen(false);
    // Optimistic mark-as-read.
    if (!n.is_read) {
      setItems((prev) =>
        prev.map((item) => (item.id === n.id ? { ...item, is_read: true } : item)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      void markRead(n.id).catch(() => {
        // Roll back the bell counter only — leave the row optimistic.
        void refetch();
      });
    }
    const dest = destinationFor(n);
    if (dest) navigate(dest);
  }

  async function handleMarkAllRead() {
    if (unreadCount === 0) return;
    const previous = items;
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try {
      await markAllRead();
    } catch (err) {
      setItems(previous);
      void refetch();
      toast.error(extractMessage(err));
    }
  }

  async function handleDelete(e: React.MouseEvent | undefined, n: NotificationRead) {
    e?.stopPropagation();
    const previous = items;
    setItems((prev) => prev.filter((item) => item.id !== n.id));
    if (!n.is_read) setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await deleteNotification(n.id);
    } catch (err) {
      setItems(previous);
      void refetch();
      toast.error(extractMessage(err));
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          className="relative rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
        >
          <motion.span
            key={shakeKey}
            animate={
              shakeKey > 0
                ? { rotate: [0, -10, 10, -5, 5, 0] }
                : { rotate: 0 }
            }
            transition={{ duration: 0.4 }}
            className="inline-flex"
          >
            <Bell className="h-5 w-5" />
          </motion.span>
          {unreadCount > 0 && (
            <motion.span
              key={`badge-${shakeKey}`}
              aria-label={`${unreadCount} unread`}
              initial={shakeKey > 0 ? { scale: 1 } : false}
              animate={
                shakeKey > 0 ? { scale: [1, 1.3, 1] } : { scale: 1 }
              }
              transition={{ duration: 0.3 }}
              className="absolute -right-0.5 -top-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold leading-none text-white"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </motion.span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[400px] overflow-hidden p-0"
      >
        <div className="flex max-h-[480px] flex-col">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-900/[0.06] bg-white px-4 py-3">
            <span className="text-base font-semibold text-slate-900">
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-500"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center px-4 py-12 text-sm text-slate-400">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center px-4 py-12 text-center">
                <Bell className="h-8 w-8 text-slate-300" />
                <p className="mt-2 text-sm text-slate-500">No notifications</p>
              </div>
            ) : (
              <ul>
                <AnimatePresence initial={false} mode="popLayout">
                  {items.map((n) => (
                    <NotificationItem
                      key={n.id}
                      notification={n}
                      compact
                      onClick={() => void handleClick(n)}
                      onDelete={(e) => void handleDelete(e, n)}
                    />
                  ))}
                </AnimatePresence>
              </ul>
            )}
          </div>

          <div className="sticky bottom-0 z-10 border-t border-slate-900/[0.06] bg-white px-4 py-2 text-center">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                if (project?.slug) {
                  navigate(`/projects/${project.slug}/notifications`);
                } else {
                  navigate("/projects");
                }
              }}
              className="text-xs font-medium text-slate-500 hover:text-slate-700"
            >
              View all notifications
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Something went wrong.";
}
